/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';

import {
    BUFFER_THRESHOLD_OFFSET,
    CALIBRATION_VALUE,
    MILLISECONDS_IN_A_SECOND,
    SECONDS_IN_A_MINUTE,
    SEEK_BACKWARD_8,
    SEEK_FORWARD_30,
} from '../../../config';
import { Log, QoEAppLogger } from '../../logging';
import { DateTime } from '../../utils';
import { PlaybackSpeed } from '../enums';
import { ITrickplayControl, IVideoPlayer } from '../interfaces';

export class TrickplayControlImpl implements ITrickplayControl {
    private static TAG: string = 'TrickplayControlImpl';

    private mIsScrubbing: boolean;

    private mPlayer: IVideoPlayer;

    private mScrubbingIntervalId: any;

    private mTrickplayModeTime: number;

    public constructor(player: IVideoPlayer) {
        this.mPlayer = player;
        this.mIsScrubbing = false;
        this.mScrubbingIntervalId = null;
        this.mTrickplayModeTime = PlaybackSpeed.SPEED_FASTFORWARD_1;
        this.speedHandler = this.speedHandler.bind(this);
    }

    private isFastForwardMode(playbackSpeed: PlaybackSpeed): boolean {
        return (
            playbackSpeed === PlaybackSpeed.SPEED_FASTFORWARD_1 ||
            playbackSpeed === PlaybackSpeed.SPEED_FASTFORWARD_2 ||
            playbackSpeed === PlaybackSpeed.SPEED_FASTFORWARD_3
        );
    }

    private isPlaybackStarted(): boolean {
        return !!(this.mPlayer.getPlayerStats()?.playTime || 0);
    }

    private isRewindMode(playbackSpeed: PlaybackSpeed): boolean {
        return (
            playbackSpeed === PlaybackSpeed.SPEED_REWIND_1 ||
            playbackSpeed === PlaybackSpeed.SPEED_REWIND_2 ||
            playbackSpeed === PlaybackSpeed.SPEED_REWIND_3
        );
    }

    private jumpScrubbingByPlaybackSpeed(playbackSpeed: PlaybackSpeed) {
        const currentPlayingTime = this.mPlayer.getPlayerCurrentTime();
        const startTime = this.mPlayer.getSeekRange().start;
        const endTime = this.mPlayer.getSeekRange().end;
        if (
            this.isRewindMode(playbackSpeed) &&
            (endTime === currentPlayingTime ||
                endTime - currentPlayingTime < BUFFER_THRESHOLD_OFFSET)
        ) {
            this.mPlayer.setPlayPosition(-(BUFFER_THRESHOLD_OFFSET + CALIBRATION_VALUE));
        }
        if (
            this.isFastForwardMode(playbackSpeed) &&
            (startTime === currentPlayingTime ||
                currentPlayingTime - startTime < BUFFER_THRESHOLD_OFFSET)
        ) {
            this.mPlayer.setPlayPosition(BUFFER_THRESHOLD_OFFSET + CALIBRATION_VALUE);
        }
    }

    private setSpeed(playbackSpeed: PlaybackSpeed): void {
        if (!this.isPlaybackStarted()) {
            return;
        }

        this.jumpScrubbingByPlaybackSpeed(playbackSpeed);
        this.mIsScrubbing = true;
        if (this.mScrubbingIntervalId) {
            clearInterval(this.mScrubbingIntervalId);
            this.mScrubbingIntervalId = null;
        }
        this.mScrubbingIntervalId = setInterval(
            this.speedHandler,
            MILLISECONDS_IN_A_SECOND,
            playbackSpeed,
        );
    }

    private speedHandler(playbackSpeed: PlaybackSpeed): void {
        const newTime =
            this.mPlayer.getPlayerCurrentTime() / MILLISECONDS_IN_A_SECOND + playbackSpeed;
        const { start, end } = this.mPlayer.getSeekRange();
        if (newTime <= start && newTime >= end) {
            if (this.isFastForwardMode(playbackSpeed)) this.goToLive();
            else this.startFromBuffer();

            this.stopScrubbing();
            Diagnostics.info(
                TrickplayControlImpl.TAG,
                'Requested position is crossing start and end seek range.',
            );
            Log.i(
                TrickplayControlImpl.TAG,
                'Requested position is crossing start and end seek range.',
            );
            return;
        }
        this.mTrickplayModeTime += Math.abs(playbackSpeed);
        this.mPlayer.setPlayPosition(playbackSpeed);
        this.mPlayer.pause(true);
    }

    public advance(): void {
        this.mPlayer.setPlayPosition(SEEK_FORWARD_30);
    }

    public fastForward(playbackSpeed: PlaybackSpeed): void {
        this.setSpeed(playbackSpeed);
    }

    public getTrickplayModeTime(): number {
        return Math.round(this.mTrickplayModeTime);
    }

    public goToLive(): void {
        Log.i(TrickplayControlImpl.TAG, 'Player at end of the buffer position');
        Diagnostics.info(TrickplayControlImpl.TAG, 'Player at end of the buffer position');
        this.mPlayer.setPlayPosition(
            Math.floor(this.mPlayer.getSeekRange().end - this.mPlayer.getPlayerCurrentTime()),
        );
    }

    public isScrubbing(): boolean {
        return this.mIsScrubbing;
    }

    public replay(): void {
        this.mPlayer.setPlayPosition(SEEK_BACKWARD_8);
    }

    public rewind(playbackSpeed: PlaybackSpeed): void {
        this.setSpeed(playbackSpeed);
    }

    public skipToTickMark(
        isForwardDirection: boolean,
        tickmarkDuration: number,
        isTimeConversionRequired: boolean,
    ): void {
        const playerCurrentTime = this.mPlayer.getPlayerCurrentTime();
        const { start, end } = this.mPlayer.getSeekRange();
        // convert epoch timestamp to absolute time in seconds
        let position = isTimeConversionRequired ? playerCurrentTime - start : playerCurrentTime;
        let remainingTime = isForwardDirection
            ? end - playerCurrentTime
            : playerCurrentTime - start;
        // add (15, 30, 60) min as per divisor calculation in playback current time
        if (isForwardDirection) {
            position += (tickmarkDuration + CALIBRATION_VALUE) * SECONDS_IN_A_MINUTE;
            // round time down to nearest multiply of divisor value, Ex: 17 round down to 15.
            position = new DateTime(position * MILLISECONDS_IN_A_SECOND)
                .floorByMinutes(tickmarkDuration)
                .getTime();
        } else {
            position -= (tickmarkDuration + CALIBRATION_VALUE) * SECONDS_IN_A_MINUTE;
            // round time up to nearest multiply of divisor value, Ex: 13 round up to 15.
            position = new DateTime(position * MILLISECONDS_IN_A_SECOND)
                .ceilByMinutes(tickmarkDuration)
                .getTime();
        }
        position /= MILLISECONDS_IN_A_SECOND;
        // convert absolute time in seconds to epoch timestamp
        position = isTimeConversionRequired ? start + position : position;
        position -= playerCurrentTime;
        if (remainingTime < tickmarkDuration * SECONDS_IN_A_MINUTE) {
            remainingTime = Math.floor(remainingTime);
            position = isForwardDirection ? remainingTime : -remainingTime;
        }
        this.mTrickplayModeTime += Math.abs(position);
        this.mPlayer.setPlayPosition(position);
    }

    public startFromBuffer(): void {
        Log.i(TrickplayControlImpl.TAG, 'Player at start of the buffer position');
        Diagnostics.info(TrickplayControlImpl.TAG, 'Player at start of the buffer position');
        this.mPlayer.setPlayPosition(
            Math.floor(this.mPlayer.getSeekRange().start - this.mPlayer.getPlayerCurrentTime()),
        );
    }

    public stopScrubbing() {
        if (!this.mIsScrubbing) return;

        this.mPlayer.pause(false);
        this.mPlayer.mute(false);
        if (this.mScrubbingIntervalId) {
            clearInterval(this.mScrubbingIntervalId);
            this.mScrubbingIntervalId = null;
        }
        this.mTrickplayModeTime = PlaybackSpeed.SPEED_FASTFORWARD_1;
        this.mIsScrubbing = false;
        QoEAppLogger.updateHealthStatsMetricsForTrickPlay();
    }
}
